/**
 * Reads a text file and returns its contents as a string
 * @param file The file to read
 * @returns Promise that resolves with the file contents
 */
export const readTextFile = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      if (event.target?.result) {
        resolve(event.target.result.toString());
      } else {
        reject(new Error("Failed to read file"));
      }
    };

    reader.onerror = (error) => {
      reject(error);
    };

    reader.readAsText(file);
  });
};

/**
 * Extracts text content from a Word document (.doc or .docx)
 * Uses mammoth.js to properly extract formatted text from Word documents
 * @param file The Word document file
 * @returns Promise that resolves with the text content
 */
export const extractTextFromDocx = async (file: File): Promise<string> => {
  // Import mammoth dynamically since it's a browser-side import
  const mammoth = await import("mammoth");

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (event) => {
      if (!event.target?.result) {
        reject(new Error("Failed to read file"));
        return;
      }

      try {
        // Convert the array buffer to the format mammoth expects
        const arrayBuffer = event.target.result as ArrayBuffer;

        // Use mammoth to extract the text
        const result = await mammoth.extractRawText({
          arrayBuffer: arrayBuffer,
        });

        resolve(result.value);
      } catch (error) {
        console.error("Error extracting text from Word document:", error);
        reject(error);
      }
    };

    reader.onerror = (error) => {
      reject(error);
    };

    // Read the file as an ArrayBuffer which is what mammoth needs
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Reads the contents of a file, whether it's a plain text file or a Word document
 * @param file The file to process
 * @returns Promise that resolves with the text content
 */
export const processFile = async (file: File): Promise<string> => {
  const fileName = file.name.toLowerCase();

  if (fileName.endsWith(".txt")) {
    return readTextFile(file);
  } else if (fileName.endsWith(".doc") || fileName.endsWith(".docx")) {
    return extractTextFromDocx(file);
  } else {
    throw new Error(
      "Unsupported file format. Please upload a .txt, .doc, or .docx file.",
    );
  }
};
